// src/redux/services/deviceApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { accumulateDevicePages } from "../../utils/devicePagination";
import {
  adaptV2DeviceDetail,
  adaptV2DeviceRow,
  adaptV2DeviceRows,
  adaptV2LatestStatus,
} from "../../utils/deviceV2Adapter";

const normalizeFleetStatusKey = (value) => {
  const key = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!key || key === "all") return "all";
  if (key === "moving" || key === "online") return "running";
  if (key === "idling" || key === "engine_on_stopped") return "idle";
  if (key === "parked" || key === "engine_off") return "stopped";
  if (
    key === "inactive" ||
    key === "offline" ||
    key === "disconnect" ||
    key === "disconnected" ||
    key === "not_reporting" ||
    key === "no_reporting"
  ) {
    return "inactive";
  }
  if (key === "nodata" || key === "unknown") return "no_data";
  return key;
};

const extractRows = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.vehicles)) return payload.vehicles;
  if (Array.isArray(payload)) return payload;
  return [];
};

const normalizePagedFleetPayload = (payload, fallback = {}) => {
  const rows = adaptV2DeviceRows(extractRows(payload));
  const page = Number(payload?.page || fallback.page || 1) || 1;
  const limit = Number(payload?.limit || fallback.limit || rows.length || 0) || 0;
  const total = Number(payload?.total || fallback.total || rows.length || 0) || 0;
  const totalPages = Number(
    payload?.totalPages ||
      fallback.totalPages ||
      (limit > 0 ? Math.ceil(total / Math.max(1, limit)) : 1)
  ) || 0;
  return {
    data: rows,
    total,
    page,
    limit,
    totalPages,
    hasMore:
      payload?.hasMore !== undefined
        ? Boolean(payload.hasMore)
        : page < totalPages,
    counts: payload?.counts || fallback.counts || null,
    filterMode: payload?.filterMode || fallback.filterMode || "none",
    source: payload?.source || fallback.source || "unknown",
  };
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");
    if (token) headers.set("x-auth-token", token);
    return headers;
  },
});

export const deviceApi = createApi({
  reducerPath: "deviceApi",
  baseQuery,
  tagTypes: ["Devices", "DeviceMeta"],
  endpoints: (builder) => ({
    // ✅ Get all devices via paginated loop — avoids slow "return all in one shot"
    // backend path which can time out for large fleets (1000+ devices).
    getDevices: builder.query({
      async queryFn(_arg, _api, _extra, fetchWithBQ) {
        const PAGE_LIMIT = 500; // server caps at 500; fewer round-trips for large fleets
        // lite=true: dashboard cards only need status counts, not live GPS/odometer.
        try {
          const all = await accumulateDevicePages({
            pageLimit: PAGE_LIMIT,
            concurrency: 4,
            allowPartialOnLaterError: true,
            fetchPage: async (page) => {
              const result = await fetchWithBQ({
                url: `/devices?page=${page}&limit=${PAGE_LIMIT}&lite=true`,
              });
              if (result.error) throw result.error;
              return result.data;
            },
          });
          return { data: all };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((d) => ({ type: "Devices", id: d._id })),
              { type: "Devices", id: "LIST" },
          ]
          : [{ type: "Devices", id: "LIST" }],
    }),

    getDeviceById: builder.query({
      query: (id) => `/devices/${id}`,
      transformResponse: (response) => response?.data || response || null,
      providesTags: (result, error, id) => [{ type: "Devices", id }],
    }),

    getMapDevicesPage: builder.query({
      query: ({ page = 1, limit = 250, ...rest } = {}) => ({
        url: "/devices/map",
        params: { page, limit, ...rest },
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        page: Number(response?.page || 1) || 1,
        limit: Number(response?.limit || 250) || 250,
        hasMore: Boolean(response?.hasMore),
      }),
      providesTags: [{ type: "Devices", id: "MAP" }],
    }),

    getDevicesV2Page: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 100,
          ...arg,
        };
        const result = await fetchWithBQ({ url: "/devices/v2", params });
        if (result.error) {
          const fallback = await fetchWithBQ({ url: "/devices", params: { ...params, lite: "true" } });
          if (fallback.error) return { error: result.error };
          const fallbackRows = Array.isArray(fallback.data?.data) ? fallback.data.data : Array.isArray(fallback.data) ? fallback.data : [];
          return {
            data: {
              data: fallbackRows,
              total: Number(fallback.data?.total || fallbackRows.length || 0),
              page: Number(fallback.data?.page || params.page || 1),
              limit: Number(fallback.data?.limit || params.limit || fallbackRows.length || 0),
              hasMore: Boolean(fallback.data?.hasMore),
              source: "v1-fallback",
            },
          };
        }

        const rows = adaptV2DeviceRows(result.data?.data);
        return {
          data: {
            data: rows,
            total: Number(result.data?.total || rows.length || 0),
            page: Number(result.data?.page || params.page || 1),
            limit: Number(result.data?.limit || params.limit || rows.length || 0),
            hasMore: Boolean(result.data?.hasMore),
            source: "v2",
          },
        };
      },
      providesTags: [{ type: "Devices", id: "LIST-V2" }],
    }),

    getDevicesV2PageServer: builder.query({
      query: (params = {}) => ({
        url: "/devices/v2/page",
        params,
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        page: Number(response?.page || 1),
        limit: Number(response?.limit || 0),
        totalPages: Number(response?.totalPages || 0),
        counts: response?.counts || null,
      }),
      providesTags: [{ type: "Devices", id: "LIST-V2-PAGE" }],
    }),

    getCompactFleetDevices: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const page = Number(arg?.page ?? 1) || 1;
        const limit = Number(arg?.limit ?? 50) || 50;
        const requestedStatus = normalizeFleetStatusKey(arg?.status);
        const search = String(arg?.search || "").trim();
        const baseParams = {
          page,
          limit,
          ...(search ? { search } : {}),
        };
        const wantsFilteredTab = requestedStatus !== "all";

        const compactPath = requestedStatus === "expired" ? "/devices/expired" : "/devices/compact";
        const compactParams = wantsFilteredTab && requestedStatus !== "expired"
          ? { ...baseParams, status: requestedStatus }
          : baseParams;

        const primary = await fetchWithBQ({ url: compactPath, params: compactParams });
        if (!primary.error) {
          const normalized = normalizePagedFleetPayload(primary.data, {
            page,
            limit,
            source: requestedStatus === "expired" ? "expired-endpoint" : "compact",
            filterMode: wantsFilteredTab ? "server" : "none",
          });
          if (normalized.data.length > 0 || normalized.total > 0 || requestedStatus === "all") {
            return { data: normalized };
          }
        }

        const v2Page = await fetchWithBQ({
          url: "/devices/v2/page",
          params: {
            ...baseParams,
            ...(wantsFilteredTab ? { status: requestedStatus } : {}),
          },
        });
        if (!v2Page.error) {
          const normalized = normalizePagedFleetPayload(v2Page.data, {
            page,
            limit,
            source: "v2-page",
            filterMode: wantsFilteredTab ? "server" : "none",
          });
          if (normalized.data.length > 0 || normalized.total > 0 || requestedStatus === "all") {
            return { data: normalized };
          }
        }

        if (wantsFilteredTab) {
          const allPages = await fetchWithBQ({
            url: "/devices/v2/page",
            params: {
              page: 1,
              limit: 250,
              ...(search ? { search } : {}),
            },
          });
          if (!allPages.error) {
            const normalizedAll = normalizePagedFleetPayload(allPages.data, {
              page: 1,
              limit: 250,
              source: "v2-all-client-filter",
              filterMode: "client",
            });
            const filtered = normalizedAll.data.filter((row) => {
              const status = normalizeFleetStatusKey(
                row?.vehicleStatus || row?.status || row?.liveData?.vehicleStatus || row?.liveData?.status
              );
              return status === requestedStatus;
            });
            const start = (page - 1) * limit;
            const paged = filtered.slice(start, start + limit);
            return {
              data: {
                data: paged,
                total: filtered.length,
                page,
                limit,
                totalPages: Math.max(1, Math.ceil(filtered.length / Math.max(1, limit))),
                hasMore: start + limit < filtered.length,
                counts: allPages.data?.counts || null,
                filterMode: "client",
                source: "v2-all-client-filter",
              },
            };
          }
        }

        const legacy = await fetchWithBQ({
          url: "/devices",
          params: {
            ...baseParams,
            includeRelations: "false",
            ...(wantsFilteredTab ? { status: requestedStatus } : {}),
          },
        });
        if (!legacy.error) {
          const normalized = normalizePagedFleetPayload(legacy.data, {
            page,
            limit,
            source: "legacy-devices",
            filterMode: wantsFilteredTab ? "server" : "none",
          });
          return { data: normalized };
        }

        return { error: primary.error || v2Page.error || legacy.error };
      },
      providesTags: [{ type: "Devices", id: "LIST-COMPACT" }],
    }),

    getFleetSummary: builder.query({
      async queryFn(_arg, _api, _extra, fetchWithBQ) {
        const primary = await fetchWithBQ({ url: "/devices/summary", params: { refresh: true } });
        if (!primary.error && primary.data) {
          return { data: primary.data };
        }

        const fallback = await fetchWithBQ({
          url: "/devices/v2/page",
          params: { page: 1, limit: 1 },
        });
        if (!fallback.error) {
          const counts = fallback.data?.counts || {};
          return {
            data: {
              total: Number(fallback.data?.total || counts.total || counts.all || 0),
              counts,
              source: "v2-page-counts",
            },
          };
        }

        return { error: primary.error || fallback.error };
      },
      providesTags: [{ type: "Devices", id: "SUMMARY" }],
    }),

    searchFleetDevices: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const q = String(arg?.q || arg?.search || "").trim();
        const limit = Number(arg?.limit ?? 25) || 25;

        const primary = await fetchWithBQ({
          url: "/devices/search",
          params: { q, limit },
        });
        if (!primary.error) {
          return {
            data: {
              data: adaptV2DeviceRows(extractRows(primary.data)),
              source: "search",
            },
          };
        }

        const fallback = await fetchWithBQ({
          url: "/devices/v2/page",
          params: { page: 1, limit, ...(q ? { search: q } : {}) },
        });
        if (!fallback.error) {
          const normalized = normalizePagedFleetPayload(fallback.data, {
            page: 1,
            limit,
            source: "v2-search-fallback",
          });
          return { data: normalized };
        }

        return { error: primary.error || fallback.error };
      },
      providesTags: [{ type: "Devices", id: "SEARCH" }],
    }),

    getDevicesV2Renewals: builder.query({
      query: (params = {}) => ({
        url: "/devices/v2/renewals",
        params,
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        page: Number(response?.page || 1),
        limit: Number(response?.limit || 0),
        totalPages: Number(response?.totalPages || 0),
        counts: response?.counts || null,
      }),
      providesTags: [{ type: "Devices", id: "LIST-V2-RENEWALS" }],
    }),

    getDeviceRenewalRequests: builder.query({
      query: (params = {}) => ({ url: "/devices/renewal-requests", params }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        page: Number(response?.page || 1),
        limit: Number(response?.limit || 0),
        totalPages: Number(response?.totalPages || 0),
      }),
      providesTags: [{ type: "Devices", id: "LIST-RENEWAL-REQUESTS" }],
    }),

    getDevicesV2Expiring: builder.query({
      query: (params = {}) => ({
        url: "/devices/v2/expiring",
        params,
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        page: Number(response?.page || 1),
        limit: Number(response?.limit || 0),
        totalPages: Number(response?.totalPages || 0),
        counts: response?.counts || null,
      }),
      providesTags: [{ type: "Devices", id: "LIST-V2-EXPIRING" }],
    }),

    getDeviceV2ById: builder.query({
      async queryFn(deviceId, _api, _extra, fetchWithBQ) {
        const result = await fetchWithBQ({ url: `/devices/v2/${deviceId}` });
        if (result.error) {
          const fallback = await fetchWithBQ({ url: `/devices/${deviceId}` });
          if (fallback.error) return { error: result.error };
          return { data: adaptV2DeviceDetail(fallback.data?.data || fallback.data || null) };
        }
        return { data: adaptV2DeviceDetail(result.data?.data || result.data || null) };
      },
      providesTags: (result, error, id) => [{ type: "Devices", id: id || result?._id }],
    }),

    getDeviceV2LatestStatus: builder.query({
      async queryFn(deviceId, _api, _extra, fetchWithBQ) {
        const result = await fetchWithBQ({ url: `/devices/v2/${deviceId}/latest-status` });
        if (result.error) {
          const fallback = await fetchWithBQ({ url: `/devices/${deviceId}/live-data` });
          if (fallback.error) return { error: result.error };
          return { data: adaptV2LatestStatus(fallback.data || {}) };
        }
        return { data: adaptV2LatestStatus(result.data?.data || result.data || {}) };
      },
    }),

    getDeviceChanges: builder.query({
      query: ({ since, limit = 250, ...rest }) => ({
        url: "/devices/changes",
        params: { since, limit, ...rest },
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        since: response?.since || null,
        returned: Number(response?.returned || 0),
        nextSince: response?.nextSince || null,
      }),
    }),

    getDeviceV2Changes: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          limit: arg?.limit ?? 200,
          ...arg,
        };
        const result = await fetchWithBQ({ url: "/devices/v2/changes", params });
        if (result.error) {
          const fallback = await fetchWithBQ({ url: "/devices/changes", params });
          if (fallback.error) return { error: result.error };
          const rows = Array.isArray(fallback.data?.data) ? fallback.data.data : [];
          return {
            data: {
              data: rows.map((row) => adaptV2DeviceRow(row)),
              since: fallback.data?.since || null,
              returned: Number(fallback.data?.returned || rows.length || 0),
              nextSince: fallback.data?.nextSince || null,
              source: "v1-fallback",
            },
          };
        }
        return {
          data: {
            data: Array.isArray(result.data?.data) ? result.data.data : [],
            since: result.data?.since || null,
            returned: Number(result.data?.returned || 0),
            nextSince: result.data?.nextSince || null,
            source: "v2",
          },
        };
      },
    }),

    // ✅ Create device (Add Device Modal)
    createDevice: builder.mutation({
      query: (payload) => ({
        url: "/devices",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Devices", id: "LIST" }],
    }),
    // ✅ Admin Create (full create)
createDeviceAdmin: builder.mutation({
  query: (payload) => ({
    url: "/devices",
    method: "POST",
    body: payload,
  }),
  invalidatesTags: [{ type: "Devices", id: "LIST" }],
}),

addMyDevice: builder.mutation({
  query: (payload) => ({
    url: "/users/devices",
    method: "POST",
    body: payload,
  }),
  invalidatesTags: [{ type: "Devices", id: "LIST" }],
}),



    // ✅ Update device (Edit modal)
    updateDevice: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/devices/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "Devices", id: arg.id },
        { type: "Devices", id: "LIST" },
      ],
    }),

    // ✅ Delete device
    deleteDevice: builder.mutation({
      query: (id) => ({
        url: `/devices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Devices", id: "LIST" }],
    }),

    // =========================
    // ✅ Modal dropdown data
    // =========================

    // ✅ Dealers (group_admin role users)
    // NOTE: this uses your already existing endpoint from userApi:
    // /users/by-role/group_admin
    // But I still kept it here for modal convenience.
    getDealers: builder.query({
      query: () => "/users/by-role/group_admin",
      transformResponse: (response) => response?.data || response || [],
      providesTags: ["DeviceMeta"],
    }),

    // ✅ Assignable users (you can pass role if needed)
    // Example: /users/by-role/user OR /users/by-role/employee
    getUsersByRole: builder.query({
      query: (role) => `/users/by-role/${role}`,
      transformResponse: (response) => response?.data || response || [],
    }),

    // ✅ Groups
    getGroups: builder.query({
      query: () => "/device-groups", // <-- change if your backend route different
      transformResponse: (response) => response?.data || response || [],
      providesTags: ["DeviceMeta"],
    }),

    // ✅ Models
    getDeviceModels: builder.query({
      query: () => "/devices/models/options",
      transformResponse: (response) => response?.options || response?.data || response || {},
      providesTags: ["DeviceMeta"],
    }),
  }),
});

export const {
  useGetDevicesQuery,
  useGetDeviceByIdQuery,
  useGetMapDevicesPageQuery,
  useLazyGetMapDevicesPageQuery,
  useGetDeviceChangesQuery,
  useLazyGetDeviceChangesQuery,
  useGetDevicesV2PageQuery,
  useLazyGetDevicesV2PageQuery,
  useGetDevicesV2PageServerQuery,
  useLazyGetDevicesV2PageServerQuery,
  useGetCompactFleetDevicesQuery,
  useLazyGetCompactFleetDevicesQuery,
  useGetFleetSummaryQuery,
  useLazyGetFleetSummaryQuery,
  useSearchFleetDevicesQuery,
  useLazySearchFleetDevicesQuery,
  useGetDevicesV2RenewalsQuery,
  useLazyGetDevicesV2RenewalsQuery,
  useGetDeviceRenewalRequestsQuery,
  useGetDevicesV2ExpiringQuery,
  useLazyGetDevicesV2ExpiringQuery,
  useGetDeviceV2ByIdQuery,
  useLazyGetDeviceV2ByIdQuery,
  useGetDeviceV2LatestStatusQuery,
  useLazyGetDeviceV2LatestStatusQuery,
  useGetDeviceV2ChangesQuery,
  useLazyGetDeviceV2ChangesQuery,
  useCreateDeviceMutation,
  useUpdateDeviceMutation,
  useDeleteDeviceMutation,

  useGetDealersQuery,
  useGetGroupsQuery,
  useGetDeviceModelsQuery,
  useGetUsersByRoleQuery,
   useCreateDeviceAdminMutation,
  useAddMyDeviceMutation,

} = deviceApi;
